import React, { useState, useMemo, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import MessageRenderer from '../chat/MessageRenderer';
import ConversationTrainingDialog from './ConversationTrainingDialog';
import RejectionDialog from './RejectionDialog';
import { extractUrls, stripMarkdown } from '../../utils/markdownUtils';
import { rejectConversation } from '../../services/api';
import { learnedQAService } from '../../services/learnedQAService';
import { toast } from 'react-toastify';
import './QAReviewModal.css';

interface QACandidate {
  id: string;
  client_id: string;
  conversation_id: string;
  question: string;
  answer: string;
  context: any;
  category: string | null;
  source_url: string | null;
  thumbs_up_count: number;
  thumbs_down_count: number;
  times_saved: number;
  status: 'pending' | 'approved' | 'rejected' | 'needs_revision';
  reviewed_by: string | null;
  reviewed_at: string | null;
  reviewer_notes: string | null;
  times_used: number;
  avg_satisfaction: number | null;
  last_used: string | null;
  created_at: string;
  training_status?: 'pending' | 'in_job' | 'fine_tuned' | 'removed' | null;
  job_id?: string | null;
}

interface ChatMessage {
  message_id: string;
  conversation_id: string;
  user_id: string;
  user_message: string;
  assistant_response: string;
  created_at: string;
  feedback_type: string | null;
  feedback_reason: string | null;
  feedback_comments: string | null;
  feedback_created_at: string | null;
  metadata: any;
}

interface QAReviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  candidates: QACandidate[];
  messages?: ChatMessage[];
  conversationId?: string;
  onApprove: (candidateId: string, notes?: string) => void;
  onReject: (candidateId: string, reason?: string) => void;
  onEditAndApprove: (
    candidateId: string,
    editedQuestion?: string,
    editedAnswer?: string,
    notes?: string
  ) => void;
  onRefresh?: () => void; // Callback to refresh candidates list
  filter?: 'all' | 'pending' | 'approved' | 'rejected';
  onFilterChange?: (filter: 'all' | 'pending' | 'approved' | 'rejected') => void;
  loading?: boolean;
  onExtractFromConversation?: () => void;
  onExtractFromMessage?: (messageId: string) => void;
  extractingFromConversation?: boolean;
  extractingFromMessage?: Set<string>;
}

const QAReviewModal: React.FC<QAReviewModalProps> = ({
  isOpen,
  onClose,
  candidates,
  messages = [],
  conversationId,
  onApprove,
  onReject,
  onEditAndApprove,
  onRefresh,
  filter = 'pending',
  onFilterChange,
  loading = false,
  onExtractFromConversation,
  onExtractFromMessage,
  extractingFromConversation = false,
  extractingFromMessage = new Set()
}) => {
  const [selectedCandidate, setSelectedCandidate] = useState<QACandidate | null>(null);
  const [collapsedBlocks, setCollapsedBlocks] = useState<Set<string>>(new Set()); // Will be populated with all block IDs (all collapsed by default)
  const [showTrainingDialog, setShowTrainingDialog] = useState(false);
  const [pendingApprovalCandidate, setPendingApprovalCandidate] = useState<string | null>(null);
  const [showConversationEdit, setShowConversationEdit] = useState(false);
  const [conversationEditText, setConversationEditText] = useState('');
  const [conversationEditNotes, setConversationEditNotes] = useState('');
  const [showRejectionDialog, setShowRejectionDialog] = useState(false);
  const [rejectionCandidateId, setRejectionCandidateId] = useState<string | null>(null);
  const [rejectionType, setRejectionType] = useState<'individual' | 'conversation'>('individual');
  const [processingAction, setProcessingAction] = useState<{
    messageId: string;
    action: 'approve' | 'reject' | 'edit' | 'similar';
  } | null>(null);
  const [pendingAction, setPendingAction] = useState<{
    messageId: string;
    action: 'approve' | 'reject' | 'edit' | 'similar';
  } | null>(null);
  const [showRemoveDialog, setShowRemoveDialog] = useState(false);
  const [removeCandidateId, setRemoveCandidateId] = useState<string | null>(null);
  const [removeWholeConversation, setRemoveWholeConversation] = useState(false);
  
  // Edit form state for individual messages
  const [editingCandidateId, setEditingCandidateId] = useState<string | null>(null);
  const [editedQuestion, setEditedQuestion] = useState('');
  const [editedAnswer, setEditedAnswer] = useState('');
  const [editNotes, setEditNotes] = useState('');

  // Debug: Log when dialog state changes
  useEffect(() => {
    if (showRemoveDialog) {
      console.log('[Remove from Training] Dialog should be visible', { removeCandidateId, removeWholeConversation });
    }
  }, [showRemoveDialog, removeCandidateId, removeWholeConversation]);

  const getFeedbackIcon = (feedbackType: string | null) => {
    switch (feedbackType) {
      case 'thumbs_up':
        return '👍';
      case 'thumbs_down':
        return '👎';
      case 'need_review':
        return '⚠️';
      default:
        return null;
    }
  };

  // Helper function to find candidate by message ID
  const findCandidateByMessageId = (messageId: string): QACandidate | null => {
    return candidates.find(c => {
      const sourceMsgId = typeof c.context === 'object' && c.context 
        ? (c.context.source_message_id || c.context.message_id)
        : null;
      return sourceMsgId === String(messageId);
    }) || null;
  };

  // Effect to handle pending actions after candidate extraction
  useEffect(() => {
    if (pendingAction) {
      const candidate = findCandidateByMessageId(pendingAction.messageId);
      if (candidate) {
        // Candidate found, perform the pending action
        const { action } = pendingAction;
        const actionToPerform = action; // Capture action before clearing state
        setPendingAction(null);
        setProcessingAction(null);

        // Use setTimeout to ensure state is cleared before performing action
        setTimeout(() => {
          if (actionToPerform === 'approve') {
            onApprove(candidate.id);
            // Clear processing state after approval
            setProcessingAction(null);
          } else if (actionToPerform === 'reject') {
            setRejectionCandidateId(candidate.id);
            setRejectionType('individual');
            setShowRejectionDialog(true);
            setProcessingAction(null);
          } else if (actionToPerform === 'edit') {
            // Show edit form instead of directly approving
            setEditingCandidateId(candidate.id);
            setEditedQuestion(candidate.question);
            setEditedAnswer(stripMarkdown(candidate.answer));
            setEditNotes('');
            setProcessingAction(null);
          } else if (actionToPerform === 'similar') {
            setSelectedCandidate(candidate);
            setProcessingAction(null);
          }
        }, 0);
      } else {
        // Candidate not found yet, set timeout to clear state if it doesn't appear
        const timeoutId = setTimeout(() => {
          // Clear states if candidate doesn't appear after 10 seconds
          setPendingAction(null);
          setProcessingAction(null);
        }, 10000);
        
        return () => clearTimeout(timeoutId);
      }
    }
  }, [candidates, pendingAction, onApprove, onEditAndApprove]);

  // Group messages into blocks (same as ChatHistory)
  const groupMessagesIntoBlocks = (msgs: ChatMessage[]) => {
    const blocks: Array<{
      id: string;
      userMessage: ChatMessage | null;
      assistantMessage: ChatMessage | null;
      timestamp: string;
    }> = [];

    const sortedMessages = [...msgs].sort((a, b) => 
      new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
    );

    let currentBlock: {
      id: string;
      userMessage: ChatMessage | null;
      assistantMessage: ChatMessage | null;
      timestamp: string;
    } | null = null;

    for (const message of sortedMessages) {
      if (message.user_message && message.user_message.trim()) {
        if (currentBlock) {
          blocks.push(currentBlock);
        }
        currentBlock = {
          id: `block-${message.message_id}`,
          userMessage: message,
          assistantMessage: null,
          timestamp: message.created_at
        };
      }
      
      if (message.assistant_response && message.assistant_response.trim() && currentBlock) {
        currentBlock.assistantMessage = message;
        if (new Date(message.created_at) > new Date(currentBlock.timestamp)) {
          currentBlock.timestamp = message.created_at;
        }
      }
    }

    if (currentBlock) {
      blocks.push(currentBlock);
    }

    // For review popup, show in chronological order (oldest first, newest last)
    // Don't reverse - keep as is (oldest first)
    return blocks;
  };

  const conversationBlocks = useMemo(() => {
    if (messages.length > 0) {
      return groupMessagesIntoBlocks(messages);
    }
    return [];
  }, [messages]);

  // Set all blocks collapsed by default when conversation blocks change
  useEffect(() => {
    if (conversationBlocks.length > 0) {
      const allBlockIds = new Set(conversationBlocks.map(block => block.id));
      setCollapsedBlocks(allBlockIds);
    }
  }, [conversationBlocks]);

  // Extract all URLs from conversation messages
  const conversationUrls = useMemo(() => {
    if (messages.length === 0) return [];
    const allUrls: string[] = [];
    messages.forEach(msg => {
      const urls = extractUrls(
        msg.assistant_response || '',
        msg.metadata?.source_url,
        msg.metadata
      );
      allUrls.push(...urls);
    });
    return Array.from(new Set(allUrls));
  }, [messages]);

  // Prepare conversation edit text when edit is clicked
  useEffect(() => {
    if (showConversationEdit && conversationBlocks.length > 0) {
      const qaPairs: string[] = [];
      conversationBlocks.forEach(block => {
        if (block.userMessage && block.assistantMessage) {
          const question = block.userMessage.user_message || '';
          const answer = stripMarkdown(block.assistantMessage.assistant_response || '');
          qaPairs.push(`Question: ${question}\n\nAnswer: ${answer}`);
        }
      });
      setConversationEditText(qaPairs.join('\n\n---\n\n'));
    }
  }, [showConversationEdit, conversationBlocks]);

  if (!isOpen) return null;

  const toggleBlockCollapse = (blockId: string) => {
    const newCollapsed = new Set(collapsedBlocks);
    if (newCollapsed.has(blockId)) {
      newCollapsed.delete(blockId);
    } else {
      newCollapsed.add(blockId);
    }
    setCollapsedBlocks(newCollapsed);
  };

  const handleApproveClick = (candidateId: string) => {
    // Show training dialog for whole conversation
    setPendingApprovalCandidate(candidateId);
    setShowTrainingDialog(true);
  };

  const handleRemoveFromTraining = async () => {
    console.log('[Remove from Training] Handler called', { removeCandidateId, removeWholeConversation });
    
    if (!removeCandidateId) {
      console.log('[Remove from Training] No candidate ID, closing dialog');
      setShowRemoveDialog(false);
      return;
    }

    try {
      const clientId = candidates.length > 0 
        ? candidates[0].client_id 
        : localStorage.getItem('client_id');
      
      console.log('[Remove from Training] Client ID:', clientId);
      
      if (!clientId) {
        toast.error('Client ID not found');
        setShowRemoveDialog(false);
        return;
      }

      console.log('[Remove from Training] Calling API...', { clientId, removeCandidateId, removeWholeConversation });
      await learnedQAService.removeFromTraining(
        clientId,
        removeCandidateId,
        removeWholeConversation
      );
      console.log('[Remove from Training] API call successful');

      toast.success(removeWholeConversation 
        ? 'Removed whole conversation from training' 
        : 'Removed from training');
      
      setShowRemoveDialog(false);
      setRemoveCandidateId(null);
      setRemoveWholeConversation(false);

      // Trigger parent to refresh candidates list
      if (onRefresh) {
        // Use dedicated refresh callback if provided
        onRefresh();
      } else if (onApprove) {
        // Fallback: trigger refresh by calling onApprove (parent will refresh candidates)
        // This is a workaround - the parent's handleApproveQA refreshes candidates
        // We call it with the removed candidate ID just to trigger the refresh logic
        setTimeout(() => {
          onApprove(removeCandidateId);
        }, 300);
      }
    } catch (error: any) {
      console.error('Error removing from training:', error);
      const errorMessage = error?.message || 'Failed to remove from training';
      toast.error(errorMessage);
    }
  };

  const handleConfirmTraining = async () => {
    if (!conversationId || !onExtractFromConversation) {
      setShowTrainingDialog(false);
      setPendingApprovalCandidate(null);
      return;
    }

    try {
      // Get client ID from candidates or localStorage
      const clientId = candidates.length > 0 
        ? candidates[0].client_id 
        : localStorage.getItem('client_id');
      
      if (!clientId) {
        toast.error('Client ID not found');
        setShowTrainingDialog(false);
        setPendingApprovalCandidate(null);
        return;
      }

      // Check for existing single approvals
      let allCandidates;
      try {
        allCandidates = await learnedQAService.getCandidates(clientId, 1000, 0);
      } catch (error: any) {
        console.error('Error fetching candidates:', error);
        const errorMessage = error?.response?.data?.detail || error?.message || 'Failed to fetch candidates';
        toast.error(typeof errorMessage === 'string' ? errorMessage : 'Failed to fetch candidates');
        setShowTrainingDialog(false);
        setPendingApprovalCandidate(null);
        return;
      }

      const allConversationCandidates = allCandidates.filter(
        (c: any) => c.conversation_id === conversationId
      );

      // Find messages with existing approved candidates that are in training
      const existingSingleApprovals = allConversationCandidates.filter((c: any) => {
        const sourceMsgId = typeof c.context === 'object' && c.context 
          ? (c.context.source_message_id || c.context.message_id)
          : null;
        return sourceMsgId && 
               c.status === 'approved' && 
               c.training_status && 
               c.training_status !== 'removed' &&
               c.training_status !== null;
      });

      // If there are existing single approvals, remove them first
      if (existingSingleApprovals.length > 0) {
        for (const candidate of existingSingleApprovals) {
          try {
            await learnedQAService.removeFromTraining(clientId, candidate.id, false);
          } catch (error: any) {
            console.error(`Error removing candidate ${candidate.id} from training:`, error);
            // Continue with other removals
          }
        }
        toast.info(`Removed ${existingSingleApprovals.length} existing single approval(s) from training`);
        // Wait a bit for the removals to process
        await new Promise(resolve => setTimeout(resolve, 500));
      }

      // First, extract Q&A from all messages with feedback in the conversation
      await onExtractFromConversation();
      
      // Wait for extraction to complete and refresh candidates
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Refresh candidates list from backend
      try {
        allCandidates = await learnedQAService.getCandidates(clientId, 1000, 0);
      } catch (error: any) {
        console.error('Error fetching candidates:', error);
        const errorMessage = error?.response?.data?.detail || error?.message || 'Failed to fetch candidates';
        toast.error(typeof errorMessage === 'string' ? errorMessage : 'Failed to fetch candidates');
        setShowTrainingDialog(false);
        setPendingApprovalCandidate(null);
        return;
      }
      
      // Get all candidates for this conversation (pending, approved, rejected)
      const updatedConversationCandidates = allCandidates.filter(
        (c: any) => c.conversation_id === conversationId
      );
      
      // Filter to only pending candidates
      const conversationCandidates = updatedConversationCandidates.filter(
        (c: any) => c.status === 'pending'
      );
      
      // Get all assistant messages (with or without feedback), but exclude those that already have approved candidates
      // Treat messages without feedback as positive feedback (thumbs up)
      const messagesToProcess = messages.filter(m => {
        // Include all messages with assistant responses
        if (!m.assistant_response || !m.assistant_response.trim() || !m.message_id) return false;
        
        // Check if this message already has an approved candidate
        const existingCandidate = updatedConversationCandidates.find((c: any) => {
          const sourceMsgId = typeof c.context === 'object' && c.context 
            ? (c.context.source_message_id || c.context.message_id)
            : null;
          return sourceMsgId === String(m.message_id) && c.status === 'approved';
        });
        
        // Only include if no approved candidate exists
        return !existingCandidate;
      });
      
      const candidateIds: string[] = [];
      
      // Find pending candidates for each message (excluding approved ones)
      // Messages without feedback are treated as positive feedback (thumbs up)
      for (const msg of messagesToProcess) {
        if (msg.message_id) {
          const candidate = conversationCandidates.find((c: any) => {
            const sourceMsgId = typeof c.context === 'object' && c.context 
              ? (c.context.source_message_id || c.context.message_id)
              : null;
            return sourceMsgId === String(msg.message_id);
          });
          if (candidate && candidate.status === 'pending') {
            candidateIds.push(candidate.id);
          }
        }
      }
      
      // Add conversation-level pending candidates (not linked to specific messages)
      conversationCandidates.forEach((c: any) => {
        const sourceMsgId = typeof c.context === 'object' && c.context 
          ? (c.context.source_message_id || c.context.message_id)
          : null;
        // Only add if not linked to a specific message or if message doesn't have approved candidate
        if (!sourceMsgId || !messagesToProcess.find(m => String(m.message_id) === sourceMsgId)) {
          if (!candidateIds.includes(c.id)) {
            candidateIds.push(c.id);
          }
        }
      });
      
      // Approve all candidates sequentially
      if (candidateIds.length > 0) {
        const reviewerId = localStorage.getItem('user_id') || 'system';
        for (const candidateId of candidateIds) {
          try {
            await learnedQAService.approveCandidate(clientId, candidateId, reviewerId);
            // Small delay between approvals
            await new Promise(resolve => setTimeout(resolve, 200));
          } catch (error: any) {
            console.error(`Error approving candidate ${candidateId}:`, error);
            const errorMessage = error?.response?.data?.detail || error?.message || `Failed to approve candidate ${candidateId}`;
            toast.error(typeof errorMessage === 'string' ? errorMessage : `Failed to approve candidate ${candidateId}`);
            // Continue with other candidates even if one fails
          }
        }
        toast.success(`Approved ${candidateIds.length} candidate(s) for training`);
      } else {
        toast.info('No pending candidates found to approve');
      }
      
      setShowTrainingDialog(false);
      setPendingApprovalCandidate(null);
      
      // Note: Parent component will refresh candidates list automatically
      // when it detects changes via the candidates prop or through other mechanisms
    } catch (error: any) {
      console.error('Error approving whole conversation:', error);
      const errorMessage = error?.response?.data?.detail || error?.message || 'Failed to approve conversation';
      toast.error(typeof errorMessage === 'string' ? errorMessage : 'Failed to approve conversation');
      setShowTrainingDialog(false);
      setPendingApprovalCandidate(null);
    }
  };

  const handleConversationEditCancel = () => {
    setShowConversationEdit(false);
    setConversationEditText('');
    setConversationEditNotes('');
  };

  const handleConversationEditSave = async () => {
    if (!conversationId || !onExtractFromConversation) {
      setShowConversationEdit(false);
      return;
    }

    try {
      // Get client ID
      const clientId = candidates.length > 0 
        ? candidates[0].client_id 
        : localStorage.getItem('client_id');
      
      if (!clientId) {
        toast.error('Client ID not found');
        setShowConversationEdit(false);
        return;
      }
      
      // Extract Q&A from conversation first
      await onExtractFromConversation();
      
      // Wait for extraction to complete and refresh candidates
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Refresh candidates list from backend
      let allCandidates;
      try {
        allCandidates = await learnedQAService.getCandidates(clientId, 1000, 0);
      } catch (error: any) {
        console.error('Error fetching candidates:', error);
        const errorMessage = error?.response?.data?.detail || error?.message || 'Failed to fetch candidates';
        toast.error(typeof errorMessage === 'string' ? errorMessage : 'Failed to fetch candidates');
        setShowConversationEdit(false);
        return;
      }
      
      const conversationCandidates = allCandidates.filter(
        (c: any) => c.conversation_id === conversationId && c.status === 'pending'
      );
      
      // Find the conversation-level candidate (or first candidate from conversation)
      const conversationCandidate = conversationCandidates.length > 0 
        ? conversationCandidates[0] 
        : null;
      
      if (conversationCandidate) {
        // Parse the edited text to extract question and answer
        const lines = conversationEditText.split('\n\n');
        let editedQuestion = '';
        let editedAnswer = '';
        
        // Try to extract question and answer from the format
        for (const line of lines) {
          if (line.startsWith('Question:')) {
            editedQuestion = line.replace('Question:', '').trim();
          } else if (line.startsWith('Answer:')) {
            editedAnswer = line.replace('Answer:', '').trim();
          }
        }
        
        // If not found in structured format, use the whole text
        if (!editedQuestion && !editedAnswer) {
          const parts = conversationEditText.split('---');
          if (parts.length >= 2) {
            editedQuestion = parts[0].replace('Question:', '').trim();
            editedAnswer = parts[1].replace('Answer:', '').trim();
          }
        }
        
        // Call edit and approve
        onEditAndApprove(
          conversationCandidate.id,
          editedQuestion || conversationCandidate.question,
          editedAnswer || conversationCandidate.answer,
          conversationEditNotes
        );
      } else {
        toast.error('No candidate found to edit. Please extract Q&A first.');
      }
      
      setShowConversationEdit(false);
      setConversationEditText('');
      setConversationEditNotes('');
    } catch (error: any) {
      console.error('Error saving conversation edit:', error);
      const errorMessage = error?.response?.data?.detail || error?.message || 'Failed to save conversation edit';
      toast.error(typeof errorMessage === 'string' ? errorMessage : 'Failed to save conversation edit');
      setShowConversationEdit(false);
    }
  };

  return (
    <>
      {/* Removal Dialog - Render at root level to ensure visibility */}
      {showRemoveDialog && (
        <div 
          className="modal-overlay" 
          onClick={() => {
            console.log('[Remove from Training] Overlay clicked, closing dialog');
            setShowRemoveDialog(false);
          }}
          style={{ zIndex: 2000, position: 'fixed' }}
        >
          <div 
            className="modal-content" 
            onClick={(e) => {
              e.stopPropagation();
              console.log('[Remove from Training] Dialog content clicked');
            }} 
            style={{ maxWidth: '500px' }}
          >
            <h3>Remove from Training</h3>
            <p>
              {removeWholeConversation 
                ? 'Are you sure you want to remove the whole conversation from training? This will remove all candidates from this conversation.'
                : 'Are you sure you want to remove this message from training? This will exclude it from future fine-tuning jobs.'}
            </p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
              <button
                className="btn-secondary"
                onClick={() => {
                  console.log('[Remove from Training] Cancel clicked');
                  setShowRemoveDialog(false);
                  setRemoveCandidateId(null);
                  setRemoveWholeConversation(false);
                }}
              >
                Cancel
              </button>
              <button
                className="btn-primary"
                onClick={(e) => {
                  e.stopPropagation();
                  console.log('[Remove from Training] Dialog Remove button clicked');
                  handleRemoveFromTraining();
                }}
                style={{ backgroundColor: '#ef4444' }}
              >
                Remove
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="qa-review-modal-overlay" onClick={onClose}>
        <div className="qa-review-modal" onClick={(e) => e.stopPropagation()}>
          {/* Header */}
          <div className="qa-review-modal-header">
            <div>
              <h2 className="qa-review-modal-title">Q&A Review</h2>
              <p className="qa-review-modal-subtitle">
                {messages.length} message{messages.length !== 1 ? 's' : ''} in conversation
              </p>
            </div>
            <button className="qa-review-modal-close" onClick={onClose}>
              <FontAwesomeIcon icon={faXmark} />
            </button>
          </div>

          {/* Conversation Actions - Top - Show for all conversations */}
          {conversationBlocks.length > 0 && (
            <div className="qa-review-conversation-actions-top">
              <div className="conversation-actions-buttons">
                <button
                  className="btn-approve-conversation"
                  onClick={() => {
                    setShowTrainingDialog(true);
                  }}
                >
                  Approve Conversation
                </button>
                <button
                  className="btn-reject-conversation"
                  onClick={() => {
                    setRejectionCandidateId(null);
                    setRejectionType('conversation');
                    setShowRejectionDialog(true);
                  }}
                >
                  Reject Conversation
                </button>
                <button
                  className="btn-edit-conversation"
                  onClick={() => {
                    setShowConversationEdit(true);
                  }}
                >
                  Edit & Approve Conversation
                </button>
                <button
                  className="btn-similar-conversation"
                  onClick={() => {
                    // Handle check similar for whole conversation
                  }}
                >
                  Check Similar Conversation
                </button>
              </div>
            </div>
          )}

          {/* Content - Scrollable */}
          <div className="qa-review-modal-content">
            {conversationBlocks.length === 0 ? (
              <div className="qa-review-empty">
                <p>No messages found in this conversation.</p>
              </div>
            ) : showConversationEdit ? (
              // Conversation Edit Form
              <div className="qa-review-conversation-edit-enhanced">
                  <div className="edit-form-header-enhanced">
                    <div className="edit-form-title-section">
                      <h4>Edit Full Conversation</h4>
                      <p className="edit-form-subtitle">Edit all Q&A pairs from this conversation. Format: Question on one line, Answer on the next line(s), separated by blank lines.</p>
                    </div>
                  </div>
                  <div className="edit-form-content">
                    <div className="form-group-enhanced">
                      <label className="form-label-enhanced">
                        <span className="label-text">Q&A Pairs</span>
                        <span className="label-required">*</span>
                      </label>
                      <textarea
                        value={conversationEditText}
                        onChange={(e) => setConversationEditText(e.target.value)}
                        rows={22}
                        className="edit-textarea-enhanced conversation-edit-textarea-enhanced"
                        placeholder="Question: What is your service?&#10;&#10;Answer: We provide comprehensive solutions...&#10;&#10;Question: How do I get started?&#10;&#10;Answer: You can start by..."
                      />
                      <p className="form-hint">Format each Q&A pair with Question and Answer separated by a blank line</p>
                    </div>
                    {conversationUrls.length > 0 && (
                      <div className="edit-form-urls-enhanced">
                        <label className="form-label-enhanced">
                          <span className="label-text">Source URLs</span>
                        </label>
                        <div className="url-list-enhanced">
                          {conversationUrls.map((url, index) => (
                            <a 
                              key={index}
                              href={url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="url-link-enhanced"
                            >
                              <span className="url-icon">🔗</span>
                              {url}
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                    <div className="form-group-enhanced">
                      <label className="form-label-enhanced">
                        <span className="label-text">Notes</span>
                        <span className="label-optional">(optional)</span>
                      </label>
                      <textarea
                        value={conversationEditNotes}
                        onChange={(e) => setConversationEditNotes(e.target.value)}
                        rows={4}
                        placeholder="Add any additional notes or context about this conversation..."
                        className="edit-textarea-enhanced"
                      />
                    </div>
                  </div>
                  <div className="edit-form-actions-enhanced">
                    <button className="btn-cancel-enhanced" onClick={handleConversationEditCancel}>
                      Cancel
                    </button>
                    <button className="btn-approve-enhanced" onClick={handleConversationEditSave}>
                      Save & Approve Conversation
                    </button>
                  </div>
              </div>
            ) : (
              <div className="qa-review-conversation-view">
                  {conversationBlocks.map((block, index) => {
                    const isCollapsed = collapsedBlocks.has(block.id);
                    // Check if message has a candidate - look for candidate with matching message_id in context
                    const candidate: QACandidate | null = block.assistantMessage?.message_id 
                      ? findCandidateByMessageId(block.assistantMessage.message_id)
                      : null;
                    
                    // Check if message is actually in training (in a job or fine-tuned)
                    // Note: 'pending' means approved but not yet in a job - these can be removed
                    const isInJob = candidate && candidate.training_status === 'in_job';
                    const isFineTuned = candidate && candidate.training_status === 'fine_tuned';
                    const isInTraining = isInJob || isFineTuned;
                    const isPending = candidate && candidate.training_status === 'pending';
                    const trainingStatus = candidate?.training_status;
                    
                    return (
                      <div 
                        key={block.id} 
                        className={`qa-review-message-block ${isInJob ? 'qa-review-in-training' : ''}`}
                        style={isInJob ? { opacity: 0.6, filter: 'blur(1px)' } : {}}
                      >
                        <div className="qa-review-block-header-wrapper">
                          <div 
                            className="qa-review-block-header"
                            onClick={() => toggleBlockCollapse(block.id)}
                            style={isFineTuned ? { backgroundColor: '#d1fae5', borderLeft: '4px solid #10b981' } : {}}
                          >
                            <span className="qa-review-block-number">#{index + 1}</span>
                            <span className="qa-review-block-preview">
                              {block.userMessage?.user_message?.substring(0, 60) || 'Message'}...
                            </span>
                            {block.assistantMessage?.feedback_type && (
                              <span className="feedback-indicator-small" title={`Feedback: ${block.assistantMessage.feedback_type}`}>
                                {getFeedbackIcon(block.assistantMessage.feedback_type)}
                              </span>
                            )}
                            {isFineTuned && (
                              <span className="qa-review-status-display" style={{
                                padding: '4px 8px',
                                borderRadius: '4px',
                                fontSize: '12px',
                                fontWeight: 500,
                                backgroundColor: '#10b981',
                                color: 'white',
                                marginLeft: '8px'
                              }}>
                                ✓ Fine-Tuned
                              </span>
                            )}
                            <span className="qa-review-block-toggle">
                              {isCollapsed ? '▼' : '▲'}
                            </span>
                          </div>
                          {/* Actions at Top - Only show for messages with thumbs_up or need_review feedback */}
                          {!isCollapsed && block.assistantMessage?.message_id &&
                           (block.assistantMessage.feedback_type === 'thumbs_up' || block.assistantMessage.feedback_type === 'need_review') && (
                            <div className="qa-review-message-actions-top">
                              {isInJob ? (
                                <>
                                  {/* Show training status for candidates currently in jobs - no remove button */}
                                  <span className="qa-review-status-display" style={{
                                    padding: '4px 8px',
                                    borderRadius: '4px',
                                    fontSize: '12px',
                                    fontWeight: 500,
                                    backgroundColor: '#f59e0b',
                                    color: 'white',
                                    textTransform: 'capitalize'
                                  }}>
                                    ⏳ In Training Job
                                  </span>
                                </>
                              ) : isFineTuned ? (
                                <>
                                  {/* Fine-tuned messages show status in header, no action needed here */}
                                </>
                              ) : isPending && candidate?.status === 'approved' ? (
                                <>
                                  {/* Show "Remove from Training" button for pending approved candidates */}
                                  <span className="qa-review-status-display" style={{
                                    padding: '4px 8px',
                                    borderRadius: '4px',
                                    fontSize: '12px',
                                    fontWeight: 500,
                                    backgroundColor: '#f59e0b',
                                    color: 'white',
                                    textTransform: 'capitalize',
                                    marginRight: '8px'
                                  }}>
                                    ⏳ Pending Training
                                  </span>
                                  <button
                                    className="btn-remove-small"
                                    onClick={async (e) => {
                                      e.stopPropagation();
                                      console.log('[Remove from Training] Button clicked', { candidateId: candidate?.id });
                                      if (!candidate?.id) {
                                        console.log('[Remove from Training] No candidate ID');
                                        return;
                                      }
                                      console.log('[Remove from Training] Setting dialog state', { candidateId: candidate.id });
                                      setRemoveCandidateId(candidate.id);
                                      setRemoveWholeConversation(false);
                                      setShowRemoveDialog(true);
                                    }}
                                    title="Remove from training"
                                    style={{
                                      backgroundColor: '#ef4444',
                                      color: 'white',
                                      border: 'none',
                                      padding: '4px 12px',
                                      borderRadius: '4px',
                                      cursor: 'pointer',
                                      fontSize: '12px'
                                    }}
                                  >
                                    Remove from Training
                                  </button>
                                </>
                              ) : candidate ? (
                                <>
                                  {candidate.status === 'pending' && (
                                    <>
                                      <button
                                        className="btn-approve-small"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          onApprove(candidate.id);
                                        }}
                                        title="Approve for training"
                                      >
                                        Approve
                                      </button>
                                      <button
                                        className="btn-reject-small"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setRejectionCandidateId(candidate.id);
                                          setRejectionType('individual');
                                          setShowRejectionDialog(true);
                                        }}
                                        title="Reject"
                                      >
                                        Reject
                                      </button>
                                      <button
                                        className="btn-edit-small"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          // Show edit form instead of directly approving
                                          setEditingCandidateId(candidate.id);
                                          setEditedQuestion(candidate.question);
                                          setEditedAnswer(stripMarkdown(candidate.answer));
                                          setEditNotes('');
                                        }}
                                        title="Edit & Approve"
                                        disabled={editingCandidateId === candidate.id}
                                      >
                                        Edit & Approve
                                      </button>
                                      <button
                                        className="btn-similar-small"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          // Check similar functionality
                                          setSelectedCandidate(candidate);
                                        }}
                                        title="Check for similar Q&A pairs to prevent duplicates before training"
                                      >
                                        Check Similar
                                      </button>
                                    </>
                                  )}
                                  {candidate.status === 'approved' && candidate.training_status && 
                                   candidate.training_status !== 'removed' && 
                                   candidate.training_status !== null ? (
                                    <>
                                      {/* Show training status and remove button for approved candidates in training */}
                                      <span className="qa-review-status-display" style={{
                                        padding: '4px 8px',
                                        borderRadius: '4px',
                                        fontSize: '12px',
                                        fontWeight: 500,
                                        backgroundColor: candidate.training_status === 'fine_tuned' ? '#6366f1' : '#f59e0b',
                                        color: 'white',
                                        textTransform: 'capitalize',
                                        marginRight: '8px'
                                      }}>
                                        {candidate.training_status === 'in_job' ? '⏳ In Training Job' : 
                                         candidate.training_status === 'fine_tuned' ? '✓ Fine-Tuned' : 
                                         '⏳ Pending Training'}
                                      </span>
                                      <button
                                        className="btn-remove-small"
                                        onClick={async (e) => {
                                          e.stopPropagation();
                                          setRemoveCandidateId(candidate.id);
                                          setRemoveWholeConversation(false);
                                          setShowRemoveDialog(true);
                                        }}
                                        title="Remove from training"
                                        style={{
                                          backgroundColor: '#ef4444',
                                          color: 'white',
                                          border: 'none',
                                          padding: '4px 12px',
                                          borderRadius: '4px',
                                          cursor: 'pointer',
                                          fontSize: '12px'
                                        }}
                                      >
                                        Remove from Training
                                      </button>
                                    </>
                                  ) : candidate.status === 'approved' ? (
                                    <span className="qa-review-status-display" style={{
                                      padding: '4px 8px',
                                      borderRadius: '4px',
                                      fontSize: '12px',
                                      fontWeight: 500,
                                      backgroundColor: '#10b981',
                                      color: 'white',
                                      textTransform: 'capitalize'
                                    }}>
                                      ✓ Approved
                                    </span>
                                  ) : candidate.status === 'rejected' ? (
                                    <span className="qa-review-status-display" style={{
                                      padding: '4px 8px',
                                      borderRadius: '4px',
                                      fontSize: '12px',
                                      fontWeight: 500,
                                      backgroundColor: '#ef4444',
                                      color: 'white',
                                      textTransform: 'capitalize'
                                    }}>
                                      ✗ Rejected
                                    </span>
                                  ) : null}
                                </>
                              ) : (
                                <>
                                  {/* Always show action buttons, extract first if needed */}
                                  <button
                                    className="btn-approve-small"
                                    onClick={async (e) => {
                                      e.stopPropagation();
                                      if (!block.assistantMessage?.message_id) return;
                                      
                                      const messageId = block.assistantMessage.message_id;
                                      
                                      // Check if candidate exists
                                      const existingCandidate = findCandidateByMessageId(messageId);
                                      if (existingCandidate) {
                                        // Candidate exists, approve directly
                                        onApprove(existingCandidate.id);
                                        return;
                                      }
                                      
                                      // No candidate, extract first
                                      if (onExtractFromMessage) {
                                        setProcessingAction({ messageId, action: 'approve' });
                                        setPendingAction({ messageId, action: 'approve' });
                                        try {
                                          await onExtractFromMessage(messageId);
                                          // The useEffect will handle the approval once candidate appears
                                        } catch (error) {
                                          console.error('Error extracting Q&A:', error);
                                          setPendingAction(null);
                                          setProcessingAction(null);
                                        }
                                      }
                                    }}
                                    disabled={extractingFromMessage.has(block.assistantMessage?.message_id || '') || 
                                             (processingAction?.messageId === block.assistantMessage?.message_id && processingAction?.action === 'approve')}
                                    title="Approve for training (will extract if needed)"
                                  >
                                    {(extractingFromMessage.has(block.assistantMessage?.message_id || '') || 
                                      (processingAction?.messageId === block.assistantMessage?.message_id && processingAction?.action === 'approve')) 
                                      ? 'Extracting...' : 'Approve'}
                                  </button>
                                  <button
                                    className="btn-reject-small"
                                    onClick={async (e) => {
                                      e.stopPropagation();
                                      if (!block.assistantMessage?.message_id) return;
                                      
                                      const messageId = block.assistantMessage.message_id;
                                      
                                      // Check if candidate exists
                                      const existingCandidate = findCandidateByMessageId(messageId);
                                      if (existingCandidate) {
                                        // Candidate exists, reject directly
                                        setRejectionCandidateId(existingCandidate.id);
                                        setRejectionType('individual');
                                        setShowRejectionDialog(true);
                                        return;
                                      }
                                      
                                      // No candidate, extract first
                                      if (onExtractFromMessage) {
                                        setProcessingAction({ messageId, action: 'reject' });
                                        setPendingAction({ messageId, action: 'reject' });
                                        try {
                                          await onExtractFromMessage(messageId);
                                          // The useEffect will handle the rejection once candidate appears
                                        } catch (error) {
                                          console.error('Error extracting Q&A:', error);
                                          setPendingAction(null);
                                          setProcessingAction(null);
                                        }
                                      }
                                    }}
                                    disabled={extractingFromMessage.has(block.assistantMessage?.message_id || '') || 
                                             (processingAction?.messageId === block.assistantMessage?.message_id && processingAction?.action === 'reject')}
                                    title="Reject (will extract if needed)"
                                  >
                                    {(extractingFromMessage.has(block.assistantMessage?.message_id || '') || 
                                      (processingAction?.messageId === block.assistantMessage?.message_id && processingAction?.action === 'reject')) 
                                      ? 'Extracting...' : 'Reject'}
                                  </button>
                                  <button
                                    className="btn-edit-small"
                                    onClick={async (e) => {
                                      e.stopPropagation();
                                      if (!block.assistantMessage?.message_id) return;
                                      
                                      const messageId = block.assistantMessage.message_id;
                                      
                                      // Check if candidate exists
                                      const existingCandidate = findCandidateByMessageId(messageId);
                                      if (existingCandidate) {
                                        // Candidate exists, show edit form
                                        setEditingCandidateId(existingCandidate.id);
                                        setEditedQuestion(existingCandidate.question);
                                        setEditedAnswer(stripMarkdown(existingCandidate.answer));
                                        setEditNotes('');
                                        return;
                                      }
                                      
                                      // No candidate, extract first
                                      if (onExtractFromMessage) {
                                        setProcessingAction({ messageId, action: 'edit' });
                                        setPendingAction({ messageId, action: 'edit' });
                                        try {
                                          await onExtractFromMessage(messageId);
                                          // The useEffect will handle the edit once candidate appears
                                        } catch (error) {
                                          console.error('Error extracting Q&A:', error);
                                          setPendingAction(null);
                                          setProcessingAction(null);
                                        }
                                      }
                                    }}
                                    disabled={(() => {
                                      const isExtracting = extractingFromMessage.has(block.assistantMessage?.message_id || '');
                                      const isProcessing = processingAction?.messageId === block.assistantMessage?.message_id && processingAction?.action === 'edit';
                                      // Check if this candidate is being edited
                                      const existingCandidate = findCandidateByMessageId(block.assistantMessage?.message_id || '');
                                      const isEditing = existingCandidate !== null && existingCandidate !== undefined && editingCandidateId === existingCandidate.id;
                                      return isExtracting || isProcessing || isEditing;
                                    })()}
                                    title="Edit & Approve (will extract if needed)"
                                  >
                                    {(extractingFromMessage.has(block.assistantMessage?.message_id || '') || 
                                      (processingAction?.messageId === block.assistantMessage?.message_id && processingAction?.action === 'edit')) 
                                      ? 'Extracting...' : 'Edit & Approve'}
                                  </button>
                                  <button
                                    className="btn-similar-small"
                                    onClick={async (e) => {
                                      e.stopPropagation();
                                      if (!block.assistantMessage?.message_id) return;
                                      
                                      const messageId = block.assistantMessage.message_id;
                                      
                                      // Check if candidate exists
                                      const existingCandidate = findCandidateByMessageId(messageId);
                                      if (existingCandidate) {
                                        // Candidate exists, check similar directly
                                        setSelectedCandidate(existingCandidate);
                                        return;
                                      }
                                      
                                      // No candidate, extract first
                                      if (onExtractFromMessage) {
                                        setProcessingAction({ messageId, action: 'similar' });
                                        setPendingAction({ messageId, action: 'similar' });
                                        try {
                                          await onExtractFromMessage(messageId);
                                          // The useEffect will handle the similar check once candidate appears
                                        } catch (error) {
                                          console.error('Error extracting Q&A:', error);
                                          setPendingAction(null);
                                          setProcessingAction(null);
                                        }
                                      }
                                    }}
                                    disabled={extractingFromMessage.has(block.assistantMessage?.message_id || '') || 
                                             (processingAction?.messageId === block.assistantMessage?.message_id && processingAction?.action === 'similar')}
                                    title="Check for similar Q&A pairs to prevent duplicates before training (will extract if needed)"
                                  >
                                    {(extractingFromMessage.has(block.assistantMessage?.message_id || '') || 
                                      (processingAction?.messageId === block.assistantMessage?.message_id && processingAction?.action === 'similar')) 
                                      ? 'Extracting...' : 'Check Similar'}
                                  </button>
                                </>
                              )}
                            </div>
                          )}
                        </div>
                        {!isCollapsed && (
                          <div className="qa-review-block-content">
                            {/* Edit Form for Individual Message */}
                            {candidate && editingCandidateId === candidate.id && (
                              <div className="qa-review-edit-form-enhanced">
                                <div className="edit-form-header-enhanced">
                                  <div className="edit-form-title-section">
                                    <h4>Edit Q&A</h4>
                                    <p className="edit-form-subtitle">Review and modify the question and answer before approving</p>
                                  </div>
                                </div>
                                <div className="edit-form-content">
                                  <div className="form-group-enhanced">
                                    <label className="form-label-enhanced">
                                      <span className="label-text">Question</span>
                                      <span className="label-required">*</span>
                                    </label>
                                    <textarea
                                      value={editedQuestion}
                                      onChange={(e) => setEditedQuestion(e.target.value)}
                                      rows={4}
                                      className="edit-textarea-enhanced"
                                      placeholder="Enter the question..."
                                    />
                                  </div>
                                  <div className="form-group-enhanced">
                                    <label className="form-label-enhanced">
                                      <span className="label-text">Answer</span>
                                      <span className="label-required">*</span>
                                    </label>
                                    <textarea
                                      value={editedAnswer}
                                      onChange={(e) => setEditedAnswer(e.target.value)}
                                      rows={10}
                                      className="edit-textarea-enhanced"
                                      placeholder="Enter the answer..."
                                    />
                                  </div>
                                  {(() => {
                                    const urls = extractUrls(candidate.answer, candidate.source_url, candidate.context);
                                    return urls.length > 0 && (
                                      <div className="edit-form-urls-enhanced">
                                        <label className="form-label-enhanced">
                                          <span className="label-text">Source URLs</span>
                                        </label>
                                        <div className="url-list-enhanced">
                                          {urls.map((url, index) => (
                                            <a 
                                              key={index}
                                              href={url} 
                                              target="_blank" 
                                              rel="noopener noreferrer"
                                              className="url-link-enhanced"
                                            >
                                              <span className="url-icon">🔗</span>
                                              {url}
                                            </a>
                                          ))}
                                        </div>
                                      </div>
                                    );
                                  })()}
                                  <div className="form-group-enhanced">
                                    <label className="form-label-enhanced">
                                      <span className="label-text">Notes</span>
                                      <span className="label-optional">(optional)</span>
                                    </label>
                                    <textarea
                                      value={editNotes}
                                      onChange={(e) => setEditNotes(e.target.value)}
                                      rows={3}
                                      placeholder="Add any additional notes or context..."
                                      className="edit-textarea-enhanced"
                                    />
                                  </div>
                                </div>
                                <div className="edit-form-actions-enhanced">
                                  <button 
                                    className="btn-cancel-enhanced" 
                                    onClick={() => {
                                      setEditingCandidateId(null);
                                      setEditedQuestion('');
                                      setEditedAnswer('');
                                      setEditNotes('');
                                    }}
                                  >
                                    Cancel
                                  </button>
                                  <button 
                                    className="btn-approve-enhanced" 
                                    onClick={() => {
                                      onEditAndApprove(
                                        candidate.id,
                                        editedQuestion !== candidate.question ? editedQuestion : undefined,
                                        editedAnswer !== candidate.answer ? editedAnswer : undefined,
                                        editNotes || undefined
                                      );
                                      setEditingCandidateId(null);
                                      setEditedQuestion('');
                                      setEditedAnswer('');
                                      setEditNotes('');
                                    }}
                                  >
                                    Save & Approve
                                  </button>
                                </div>
                              </div>
                            )}
                            
                            {block.userMessage && (
                              <div className="qa-review-user-message">
                                <strong>User:</strong>
                                <div className="qa-review-message-text">
                                  {block.userMessage.user_message}
                                </div>
                              </div>
                            )}
                            {block.assistantMessage && (
                              <div className="qa-review-assistant-message">
                                <strong>Assistant:</strong>
                                <div className="qa-review-message-text">
                                  <MessageRenderer
                                    content={block.assistantMessage.assistant_response}
                                    messageId={block.assistantMessage.message_id}
                                  />
                                </div>
                                {block.assistantMessage.feedback_type && (
                                  <div className="qa-review-feedback-badge">
                                    Feedback: {block.assistantMessage.feedback_type}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Conversation Training Dialog */}
      {showTrainingDialog && (
        <ConversationTrainingDialog
          isOpen={showTrainingDialog}
          onClose={() => {
            setShowTrainingDialog(false);
            setPendingApprovalCandidate(null);
          }}
          onConfirm={handleConfirmTraining}
          messageCount={messages.length}
          conversationId={conversationId || ''}
          hasFeedbackMessages={messages.length > 0}
        />
      )}


      {/* Rejection Dialog */}
      <RejectionDialog
        isOpen={showRejectionDialog}
        onClose={() => {
          setShowRejectionDialog(false);
          setRejectionCandidateId(null);
        }}
        onConfirm={async (reason) => {
          if (rejectionCandidateId) {
            onReject(rejectionCandidateId, reason);
          } else if (rejectionType === 'conversation') {
            // Handle conversation rejection
            try {
              const clientId = candidates.length > 0 
                ? candidates[0].client_id 
                : (messages.length > 0 ? localStorage.getItem('client_id') : null);
              
              if (!clientId || !conversationId) {
                toast.error('Missing client ID or conversation ID');
                return;
              }
              
              await rejectConversation(clientId, conversationId, reason);
              toast.success('Conversation rejected');
              // Optionally refresh or close modal
            } catch (error: any) {
              console.error('Error rejecting conversation:', error);
              toast.error(error.response?.data?.detail || 'Failed to reject conversation');
            }
          }
          setShowRejectionDialog(false);
          setRejectionCandidateId(null);
        }}
        title={rejectionType === 'conversation' ? 'Reject Conversation' : 'Reject Q&A Candidate'}
        message={rejectionType === 'conversation' 
          ? 'Please provide a reason for rejecting this conversation:'
          : 'Please provide a reason for rejection:'}
      />
    </>
  );
};

export default QAReviewModal;

